/* eslint-disable @typescript-eslint/no-unused-vars */
import { useQuery } from '@apollo/client'
import { Hidden } from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'
import React from 'react'
import { useParams } from 'react-router-dom'

import { ACCOMMODATIONS_QUERY } from '../../../graphql/accommodation'
import { DateObject } from '../../../helpers/date'
import { lang } from '../../../localization'
import { Accommodation } from '../../../types/accommodation'
import { DestinationOption } from '../../../types/accommodationSubscribedToChannel'
import { SearchAvailabilityInput } from '../../../types/searchAvailability'
import SearchWidgetDesktopInterface from './SearchWidgetDesktop/SearchWidgetDesktopInterface'
import SearchWidgetMobileInterface from './SearchWidgetMobile/SearchWidgetMobileInterface'
import SearchWidgetTabletInterface from './SearchWidgetTablet/SearchWidgetTabletInterface'

interface SearchWidgetMainProps {
  setSearchAvailabilityInput: React.Dispatch<
    React.SetStateAction<SearchAvailabilityInput>
  >
  searchAvailabilityInput: SearchAvailabilityInput
  readMode?: boolean
  setreadMode: React.Dispatch<React.SetStateAction<boolean>>
  isInResultPage?: boolean
}

const SearchWidgetMain: React.FC<SearchWidgetMainProps> = ({
  setSearchAvailabilityInput,
  searchAvailabilityInput,
  readMode,
  setreadMode,
  isInResultPage
}) => {
  const params = useParams<{ channelId: string; code: string; name: string }>()

  const [selectRangeModalIsOpen, setSelectRangeModalIsOpen] =
    useState<boolean>(false)
  const [dateObject, setdateObject] = useState<DateObject>({
    gregorianStartDate: searchAvailabilityInput.arrival,
    gregorianEndDate: searchAvailabilityInput.departure
  })

  const [guestsNumber, setguestsNumber] = useState<number>(
    searchAvailabilityInput.guestsAges.length
  )
  const [selectNumberOfGuestsModalIsOpen, setSelectNumberOfGuestsModalIsOpen] =
    useState<boolean>(false)
  const [options, setOptions] = useState<DestinationOption[]>([])
  const datePickerRef = useRef<HTMLDivElement | null>(null)
  const guestInfoRef = useRef<HTMLDivElement | null>(null)

  const { data: accommodations, loading: loadingForAccommodation } = useQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      channelCode: params.code
    }
  })

  useEffect(() => {
    const accommodationsOptions: DestinationOption[] =
      accommodations?.accommodations.map((acc) => ({
        destinationName: acc.basicDetails.name,
        destinationCode: acc.basicDetails.propertyCode,
        isCity: false,
        cityID: undefined
      })) || []

    const cityOptions: DestinationOption[] = Array.from(
      new Map(
        (accommodations?.accommodations ?? [])
          .filter((acc) => typeof acc.basicDetails.city === 'string')
          .map(
            (acc) =>
              [
                acc.basicDetails.city as string,
                {
                  destinationName: acc.basicDetails.city as string,
                  destinationCode: undefined,
                  isCity: true,
                  cityID: acc.basicDetails.cityID
                }
              ] as [string, DestinationOption]
          )
      ).values()
    )

    setOptions([...cityOptions, ...accommodationsOptions])
  }, [loadingForAccommodation])

  useEffect(() => {
    setSearchAvailabilityInput((prev) => ({
      ...prev,
      arrival: dateObject.gregorianStartDate || '',
      departure: dateObject.gregorianEndDate || '',
      guestsAges: Array(guestsNumber).fill(24)
    }))
  }, [dateObject, guestsNumber])

  const useEffectFunction = () => {
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef &&
        datePickerRef?.current &&
        !datePickerRef.current?.contains(event.target as Node) &&
        selectRangeModalIsOpen
      ) {
        setSelectRangeModalIsOpen(false)
      }
      if (
        guestInfoRef.current &&
        !guestInfoRef?.current?.contains(event.target as Node) &&
        selectNumberOfGuestsModalIsOpen
      ) {
        setSelectNumberOfGuestsModalIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }

  const SearchWidgetDesktopInterfaceFunction = () => {
    return (
      <SearchWidgetDesktopInterface
        effectFunction={useEffectFunction}
        type={lang === 'fa' ? 'jalaali' : 'gregorian'}
        {...{
          readMode,
          setreadMode,
          options,
          searchAvailabilityInput,
          guestInfoRef,
          datePickerRef,
          setguestsNumber,
          guestsNumber,
          setdateObject,
          dateObject,
          selectNumberOfGuestsModalIsOpen,
          selectRangeModalIsOpen,
          setSelectNumberOfGuestsModalIsOpen,
          setSelectRangeModalIsOpen,
          setSearchAvailabilityInput,
          loadingForAccommodation
        }}
      />
    )
  }
  const SearchWidgetMobileInterfaceFunction = () => {
    return (
      <SearchWidgetMobileInterface
        selectRangeModalIsOpen={selectRangeModalIsOpen}
        setSelectRangeModalIsOpen={setSelectRangeModalIsOpen}
        selectNumberOfGuestsModalIsOpen={selectNumberOfGuestsModalIsOpen}
        setSelectNumberOfGuestsModalIsOpen={setSelectNumberOfGuestsModalIsOpen}
        dateObject={dateObject}
        setdateObject={setdateObject}
        guestsNumber={guestsNumber}
        setguestsNumber={setguestsNumber}
        datePickerRef={datePickerRef}
        guestInfoRef={guestInfoRef}
        effectFunction={useEffectFunction}
        type={lang === 'fa' ? 'jalaali' : 'gregorian'}
        searchAvailabilityInput={searchAvailabilityInput}
        setSearchAvailabilityInput={setSearchAvailabilityInput}
        options={options}
        setreadMode={setreadMode}
        readMode={readMode}
        loadingForAccommodation={loadingForAccommodation}
        isInResultPage={isInResultPage}
      />
    )
  }
  const SearchWidgetTabletInterfaceFunction = () => {
    return (
      <SearchWidgetTabletInterface
        selectRangeModalIsOpen={selectRangeModalIsOpen}
        setSelectRangeModalIsOpen={setSelectRangeModalIsOpen}
        selectNumberOfGuestsModalIsOpen={selectNumberOfGuestsModalIsOpen}
        setSelectNumberOfGuestsModalIsOpen={setSelectNumberOfGuestsModalIsOpen}
        dateObject={dateObject}
        setdateObject={setdateObject}
        guestsNumber={guestsNumber}
        setguestsNumber={setguestsNumber}
        datePickerRef={datePickerRef}
        guestInfoRef={guestInfoRef}
        effectFunction={useEffectFunction}
        type={lang === 'fa' ? 'jalaali' : 'gregorian'}
        searchAvailabilityInput={searchAvailabilityInput}
        setSearchAvailabilityInput={setSearchAvailabilityInput}
        options={options}
        setreadMode={setreadMode}
        readMode={readMode}
        loadingForAccommodation={loadingForAccommodation}
      />
    )
  }

  return (
    <>
      <Hidden smDown>{SearchWidgetDesktopInterfaceFunction()}</Hidden>
      <Hidden xsDown mdUp>
        {SearchWidgetTabletInterfaceFunction()}
      </Hidden>
      <Hidden smUp>{SearchWidgetMobileInterfaceFunction()}</Hidden>
    </>
  )
}
export default SearchWidgetMain
